import type { AnimationEvent } from '@angular/animations';
import { InjectionToken, type Type } from '@angular/core';
import type { RequestError } from '@freelancer/datastore';
import type { BackendErrorResponse } from '@freelancer/freelancer-http';
import type { ButtonColor } from '@freelancer/ui/button';
import type {
  LinkColor,
  LinkHoverColor,
  LinkUnderline,
} from '@freelancer/ui/link';
import type { FontColor } from '@freelancer/ui/text';

export const TOAST_ALERT_COMPONENT = new InjectionToken<any>(
  'TOAST_ALERT_COMPONENT',
);

export enum ToastAlertType {
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
  LOADING = 'loading',
}

export enum ToastAlertColor {
  LIGHT = 'light',
  DARK = 'dark',
}

export interface ToastAlertComponentInterface {
  element: HTMLElement;
  state: string;

  // Inputs
  closeable: boolean;
  color: ToastAlertColor;
  content?: string;
  action?: DynamicToastAlertAction;
  id: string;
  timeout?: number;
  type: ToastAlertType;
  error?: GenericError;
  fontColor?: FontColor;
  customComponentConfig?: CustomComponentConfig;

  handleClose(): void;
  move(position: number): void;
  startTimer(): void;
  resetTimer(): void;
  unsubscribeTimer(): void;
  toggleVisibility(visibility: string): void;
  handleAnimationDone(event: AnimationEvent): void;
}

export type DynamicToastAlertAction =
  | DynamicToastAlertLinkAction
  | DynamicToastAlertButtonAction;

export interface DynamicToastAlertLinkAction {
  readonly type: 'link';
  readonly text: string;
  readonly url: string;
  readonly trackingLabel: string;
  readonly trackingSection: string;
  readonly queryParams?: Record<string, string>;
  readonly color?: LinkColor;
  readonly hoverColor?: LinkHoverColor;
  readonly underline?: LinkUnderline;
}

export interface DynamicToastAlertButtonAction {
  readonly type: 'button';
  readonly text: string;
  readonly trackingLabel: string;
  readonly trackingSection: string;
  onClick(): void;
  readonly color?: ButtonColor; // Optional color, defaults to TEXT_BLUE
}

export interface DynamicToastAlertOptions {
  message?: string;
  id?: string;
  action?: DynamicToastAlertAction;
  type?: ToastAlertType;
  color?: ToastAlertColor;
  indefinite?: boolean;
  timeout?: number;
  closeable?: boolean;
  error?: GenericError;
  fontColor?: FontColor;
  reopenIfExists?: boolean;
}

export interface CustomComponentConfig<TInputs = Record<string, any>> {
  component: Type<any>;
  inputs?: Partial<TInputs>;
}

export interface GenericError {
  error: BackendErrorResponse<any> | RequestError<any>;
  message?: string;
  retry?(): void;
}
